import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import coinIcon from "../../images/site_logo/TC-Logo-icon.png";
import "./style.css";
import { API_URL } from "../../constant";

function RetailBusiness(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [loginData, setLoginData] = useState(null);
  
  // Ref for the auto-dismiss timeout
  const autoDismissRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Clear any existing auto-dismiss timeout
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }
    
    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await fetch(`${API_URL}/api/retail-business/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed. Please try again.");
      }

      // Login successful
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("user_type", "retail_business");
      setLoginData(data);

      // Get user status after successful login
      const statusResponse = await fetch(
        `${API_URL}/api/retail-business/status`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${data.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!statusResponse.ok) {
        throw new Error("Failed to fetch user status");
      }

      const statusData = await statusResponse.json();
      setUserStatus(statusData.user_status);

      setMessage({
        text: data.message || "Login successful!",
        type: "success",
      });

      // Handle different statuses
      handleUserStatus(statusData.user_status, data.access_token);
      
    } catch (error) {
      console.error("Login error:", error);
      
      setMessage({
        text: error.message || "Network error. Please try again.",
        type: "error",
      });
      
      // Clear auth data on error
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_type");
      setLoginData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const openInNewTab = (url) => {
    try {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      
      if (newWindow) {
        return newWindow;
      }
      
      // Fallback for browsers that block window.open
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return { closed: true };
    } catch (error) {
      console.error('Error opening new tab:', error);
      window.location.href = url;
      return { closed: true };
    }
  };

  const handleUserStatus = (status, token) => {
    switch (status) {
      case "active":
      case "approved":
        // Generate a token key for cross-tab communication
        const tempTokenKey = `business_temp_token_${Date.now()}`;
        localStorage.setItem(tempTokenKey, token);
        
        // Build wallet URL with token parameter
        const walletUrl = `${window.location.origin}/retail-business/wallet?temp_token_key=${tempTokenKey}&just_logged_in=true`;

        // Open wallet in new tab
        const newWindow = openInNewTab(walletUrl);
        
        // Set a timeout to send message to new tab
        setTimeout(() => {
          try {
            if (newWindow && newWindow.postMessage) {
              newWindow.postMessage(
                { 
                  type: "LOGIN_SUCCESS", 
                  token: token,
                  tempTokenKey: tempTokenKey
                },
                window.location.origin
              );
            }
          } catch (e) {
            console.log("Could not send message to wallet tab:", e);
          }
          
          // Clean up temp token after 30 seconds
          setTimeout(() => {
            localStorage.removeItem(tempTokenKey);
          }, 30000);
        }, 500);

        // Clear form fields
        setEmail("");
        setPassword("");
        
        // Set auto-dismiss timeout for success message
        autoDismissRef.current = setTimeout(() => {
          setMessage({ text: "", type: "" });
        }, 15000); // 15 seconds
        break;

      case "awaiting":
        setShowStatusPopup(true);
        break;

      default:
        // For other non-approved statuses, show error
        setMessage({
          text: `Your account status is "${status}". Please contact support for assistance.`,
          type: "error",
        });
        // Clear auth data
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_type");
        setLoginData(null);
        break;
    }
  };

  const handleOpenWallet = () => {
    if (loginData && loginData.access_token && userStatus !== "awaiting") {
      // Reopen wallet with stored token
      const tempTokenKey = `business_temp_token_${Date.now()}`;
      localStorage.setItem(tempTokenKey, loginData.access_token);
      const walletUrl = `${window.location.origin}/retail-business/wallet?temp_token_key=${tempTokenKey}`;
      openInNewTab(walletUrl);
    }
  };

  const handleClosePopup = () => {
    setShowStatusPopup(false);
    // If status is awaiting, clear the stored auth data
    if (userStatus === "awaiting") {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_type");
      setLoginData(null);
      setUserStatus("");
      setEmail("");
      setPassword("");
    }
  };

  const handleDismissMessage = () => {
    // Clear the auto-dismiss timeout if it exists
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }
    setMessage({ text: "", type: "" });
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (autoDismissRef.current) {
        clearTimeout(autoDismissRef.current);
      }
    };
  }, []);

  const messageStyle = {
    padding: "10px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
  };

  // Status Popup Component
  const StatusPopup = () => {
    if (!showStatusPopup) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "15px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            maxWidth: "500px",
            width: "90%",
            textAlign: "center",
            position: "relative",
          }}
        >
          <button
            onClick={handleClosePopup}
            style={{
              position: "absolute",
              top: "15px",
              right: "15px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ×
          </button>

          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#FFF3CD",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto 20px",
                border: "3px solid #FFC107",
              }}
            >
              <span style={{ fontSize: "36px", color: "#856404" }}>⏳</span>
            </div>
            <h3 style={{ color: "#856404", marginBottom: "15px" }}>
              Profile Under Review
            </h3>
          </div>

          <p style={{ color: "#666", lineHeight: "1.6", marginBottom: "25px" }}>
            Thank you for logging in! Your retail business profile is currently
            under review by our team. This process usually takes 24-48 hours. We
            will notify you via email once your account has been approved.
          </p>

          <div
            style={{
              backgroundColor: "#E7F3FF",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #B3D9FF",
            }}
          >
            <p style={{ color: "#004085", margin: 0, fontSize: "14px" }}>
              <strong>What's next?</strong> Once approved, you'll gain full
              access to your business dashboard where you can set up rewards,
              track customer engagement, and manage your loyalty programs.
            </p>
          </div>

          <button
            onClick={handleClosePopup}
            style={{
              backgroundColor: "#2D4CA8",
              color: "white",
              border: "none",
              padding: "12px 30px",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Understand
          </button>

          <div
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#999",
            }}
          >
            You will be able to access the portal once your account is approved.
          </div>
        </div>
      </div>
    );
  };

  // Success Message with Wallet Button Component
  const SuccessMessage = () => {
    if (message.type !== "success" || userStatus === "awaiting") return null;

    return (
      <div
        style={{
          ...messageStyle,
          backgroundColor: "#e8f5e9",
          color: "#2e7d32",
          border: "1px solid #c8e6c9",
          textAlign: "center",
        }}
      >
        <p style={{ margin: "0 0 0px 0" }}>
          {message.text}
          <button
            onClick={handleDismissMessage}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              marginLeft: "10px",
              cursor: "pointer",
              float: "right",
              fontSize: "16px",
            }}
            title="Dismiss message"
          >
            ×
          </button>
        </p>
        <button
          onClick={handleOpenWallet}
          style={{
            backgroundColor: "#2D4CA8",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "14px",
            marginTop: "5px",
          }}
        >
          Open Business Portal in New Tab
        </button>
      </div>
    );
  };

  return (
    <section
      id="id_ico_about_section"
      className={`bg-white justify-content-center d-flex align-items-center ${props.abClass}`}
      style={{ color: "#000" }}
    >
      <div className="container mt-5 mb-5">
        <div className="row justify-content-center align-items-center">
          {/* Text Content Section */}
          <div className="col-lg-7 mb-5 mb-lg-0 mt-5">
            <div>
              <h1
                className="hero_title mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                Welcome to TopCoin UK - Boost Your Retail Business with Loyalty
                Rewards!
              </h1>
              <p className="mb-4">
                As a Retail Business Partner with TopCoin UK, you'll unlock
                powerful tools to increase customer loyalty, drive repeat
                business, and grow your revenue through our innovative rewards
                ecosystem.
              </p>

              <h3
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                Benefits for Your Retail Business:
              </h3>

              <p className="mb-3">
                Join thousands of successful retailers who are transforming
                their customer relationships:
              </p>

              <ul className="mb-4">
                <li>
                  <strong>Increase Customer Retention</strong> - Keep customers
                  coming back with attractive rewards
                </li>
                <li>
                  <strong>Drive More Sales</strong> - Encourage higher spending
                  through tiered rewards system
                </li>
                <li>
                  <strong>Easy Integration</strong> - Simple setup that works
                  with your existing POS system
                </li>
                <li>
                  <strong>Real-time Analytics</strong> - Track customer
                  engagement and redemption patterns
                </li>
                <li>
                  <strong>Cost-effective Marketing</strong> - Reduce customer
                  acquisition costs through loyalty
                </li>
                <li>
                  <strong>Flexible Reward Options</strong> - Customize rewards
                  that fit your business model
                </li>
              </ul>

              <h3
                className="mb-3"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ color: "#000" }}
              >
                How It Works for Retailers:
              </h3>

              <ul className="mb-4">
                <li>Register your retail business and complete verification</li>
                <li>Set up your reward parameters and referral reward structure</li>
                <li>Integrate with your payment system (optional)</li>
                <li>Start issuing TopCoin UK rewards to your customers</li>
                <li>
                  Track performance through your dedicated business dashboard
                </li>
                <li>Receive referral-based rewards for successful introductions</li>
              </ul>
            </div>
          </div>

          {/* Login Form Section */}
          <div className="col-lg-5 position-relative">
            <div
              className="bg-white text-center shadow p-4"
              style={{
                color: "#000",
                borderRadius: "20px",
              }}
            >
              <div className="d-flex justify-content-center mb-3">
                <img
                  src={coinIcon}
                  alt="TopCoin UK"
                  style={{ width: 150, height: 150 }}
                />
              </div>

              <h4
                className="fw-bold mb-2"
                style={{
                  background:
                    "linear-gradient(90deg, #2D4CA8 52%, #9F4AFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
              >
                TopCoin UK
                <br />
                Retail Business <br /> Portal
              </h4>
              <h3 className="sub-title">Enter your Email and Password</h3>

              {/* Success Message with Wallet Button */}
              <SuccessMessage />

              {/* Regular Message display */}
              {message.text && message.type !== "success" && (
                <div
                  style={{
                    ...messageStyle,
                    backgroundColor:
                      message.type === "error" ? "#ffebee" : "#e8f5e9",
                    color: message.type === "error" ? "#c62828" : "#2e7d32",
                    border:
                      message.type === "error"
                        ? "1px solid #ffcdd2"
                        : "1px solid #c8e6c9",
                  }}
                >
                  {message.text}
                  <button
                    onClick={handleDismissMessage}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      marginLeft: "10px",
                      cursor: "pointer",
                      float: "right",
                      fontSize: "16px",
                    }}
                    title="Dismiss message"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleLogin} className="text-start mb-4">
                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ color: "#052C65" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control bg-white"
                    placeholder="Your registered business email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    style={{
                      borderRadius: "8px",
                      padding: "12px",
                      border: "1px solid #ccc",
                      color: "black",
                    }}
                  />
                </div>

                <div className="mb-2 position-relative">
                  <label
                    className="form-label fw-semibold"
                    style={{ color: "#052C65" }}
                  >
                    Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="form-control bg-white"
                      placeholder="Your account password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      style={{
                        borderRadius: "8px",
                        padding: "12px",
                        border: "1px solid #ccc",
                        color: "black",
                        paddingRight: "40px",
                      }}
                    />
                    <span
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "12px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#666",
                        zIndex: 2,
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {passwordVisible ? (
                        <FaEye size={18} />
                      ) : (
                        <FaEyeSlash size={18} />
                      )}
                    </span>
                  </div>
                </div>

                <div className="mb-4 text-end">
                  <a
                    href="/forgot-password-r"
                    style={{
                      color: "#2D4CA8",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>

                <button
                  type="submit"
                  className="w-100 text-white fw-bold py-2"
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#666" : "#0000A0",
                    borderRadius: "8px",
                    border: "none",
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </button>
              </form>

              {/* <div className="mt-4">
                <p className="mb-0" style={{ color: "#666" }}>
                  New to TopCoin UK for Businesses?{" "}
                  <a
                    href="/retail-business-signup"
                    target="_blank"
                    style={{
                      color: "#2D4AFF",
                      textDecoration: "none",
                      fontWeight: "bold",
                    }}
                  >
                    Register Your Retail Business
                  </a>
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Status Popup */}
      <StatusPopup />
    </section>
  );
}

export default RetailBusiness;